import { useCallback } from "react";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import {
  issueSnapshotFailure,
  issueSnapshotUploadSuccess,
} from "@adsk/acc-issues-core/state/redux/ducks/issueSnapshots";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { useSdk } from "@adsk/acc-issues-core/state/sdk/useSdk";
import { Issue } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { IssueSnapshotCreateTrigger } from "../../../../utils/analytics/analyticsLib093IssuesEvents";
import { trackIssueSnapshotCreate } from "../../../../utils/analytics/issueActionsTracking";
import { fetchIssueByIdAction } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import { useSnapshotAbortController } from "./useSnapshotAbortController";

import { useIssuePlacementType } from "@adsk/acc-issues-core/utils/hooks/useIssuePlacementType";

export const useSetSnapshotAsAThumbnail = () => {
  const dispatch = useDispatch();
  const { showNotification } = useNotifications();
  const { T } = useTranslations();
  const { throwIfAborted, isAbortError } = useSnapshotAbortController();
  const sdk = useSdk();
  const getIssuePlacementType = useIssuePlacementType();

  return useCallback(
    async ({
      issue,
      storageUrn,
      hasMarkups,
      trigger,
      flowId,
      onSuccess,
      handleFailureNotification,
    }: {
      issue: Issue;
      storageUrn: string | undefined;
      hasMarkups: boolean;
      trigger: IssueSnapshotCreateTrigger;
      flowId: string;
      onSuccess?: () => Promise<void>;
      handleFailureNotification?: () => void;
    }) => {
      if (!storageUrn) {
        return;
      }

      try {
        // Avoid creating the snapshot if the flow was aborted
        throwIfAborted(flowId);

        const linkPromise = sdk.Oss.getDownloadLink(storageUrn);
        const issueSnapshotPromise = sdk.IssueSnapshots.create(
          issue.id,
          storageUrn,
          hasMarkups
        );

        const [downloadLink] = await Promise.all([
          linkPromise,
          issueSnapshotPromise,
        ]);

        // Avoid updating the state if the flow was aborted
        throwIfAborted(flowId);

        dispatch(
          issueSnapshotUploadSuccess({
            issueId: issue.id,
            snapshotUrl: downloadLink.url,
            snapshotUrn: storageUrn,
            hasMarkups,
            flowId,
          })
        );

        // Once we created the snapshot of the Issue, need to re-fetch it
        dispatch(
          fetchIssueByIdAction({
            id: issue.id,
          })
        );

        const placementType = getIssuePlacementType(issue);
        trackIssueSnapshotCreate({
          issueId: issue.id,
          trigger,
          placementType,
        });

        await onSuccess?.();
      } catch (e) {
        if (isAbortError(e)) {
          return;
        }
        dispatch(issueSnapshotFailure({ issueId: issue.id }));
        if (handleFailureNotification) {
          handleFailureNotification();
        } else {
          showNotification({
            status: NOTIFICATION_STATUSES.ERROR,
            message: T.translate("thumbnailField.failureMessage"),
          });
        }
      }
    },
    [
      throwIfAborted,
      sdk.Oss,
      sdk.IssueSnapshots,
      dispatch,
      getIssuePlacementType,
      isAbortError,
      showNotification,
      T,
    ]
  );
};
